import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok Pixel tracking helper
const trackTikTokEvent = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.ttq) {
    window.ttq.track(eventName, eventData);
  }
};

// Google Analytics 4 tracking helper
const trackGA4Event = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventData);
  }
};

// Meta Pixel tracking helper
const trackMetaEvent = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, eventData);
  }
};

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, email_sent, error
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const processPayment = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        setStatus('error');
        setErrorMessage('No payment session found. Please try again.');
        return;
      }

      try {
        // Poll for payment status and trigger magic link
        const response = await fetch(`${API_URL}/api/payment/checkout-status/${sessionId}`);

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to verify payment');
        }

        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setEmail(data.email);
          setStatus('email_sent');
          
          // Track TikTok Purchase Event
          trackTikTokEvent('CompletePayment', {
            content_type: 'product',
            content_id: 'washed-away-stream',
            content_name: 'Washed Away Documentary Stream',
            quantity: 1,
            price: 12.69,
            value: 12.69,
            currency: 'AUD'
          });
          
          // Track Meta Pixel Purchase Event
          trackMetaEvent('Purchase', {
            content_type: 'product',
            content_ids: ['washed-away-stream'],
            content_name: 'Washed Away Documentary Stream',
            num_items: 1,
            value: 12.69,
            currency: 'AUD'
          });
          
          // Track GA4 Purchase Event
          trackGA4Event('purchase', {
            transaction_id: searchParams.get('session_id'),
            value: 12.69,
            currency: 'AUD',
            items: [{
              item_id: 'washed-away-stream',
              item_name: 'Washed Away Documentary Stream',
              price: 12.69,
              quantity: 1
            }]
          });
          
          toast.success('Payment successful! Check your email for access.');
        } else if (data.payment_status === 'unpaid') {
          // Payment not yet complete, might need to retry
          setStatus('error');
          setErrorMessage('Payment was not completed. Please try again.');
        } else {
          setStatus('error');
          setErrorMessage('Payment status unknown. Please contact support.');
        }

      } catch (error) {
        console.error('Payment verification error:', error);
        setStatus('error');
        setErrorMessage(error.message || 'Failed to verify payment. Please contact support.');
        toast.error('Payment verification failed');
      }
    };

    processPayment();
  }, [searchParams]);

  return (
    <div className="success-page">
      <div className="success-container">
        <div className="success-card">
          {/* Logo */}
          <img 
            src="https://customer-assets.emergentagent.com/job_washed-away-cinema/artifacts/zlh4k7vd_LOGO.png" 
            alt="Washed Away"
            className="success-logo"
          />

          {/* Status Display */}
          {status === 'processing' && (
            <>
              <div className="status-icon">
                <Loader2 className="h-16 w-16 animate-spin text-electric-blue" />
              </div>
              <h1 className="success-title">Processing Payment...</h1>
              <p className="success-message">
                Please wait while we confirm your payment.
              </p>
            </>
          )}

          {status === 'email_sent' && (
            <>
              <div className="status-icon">
                <div className="email-icon-container">
                  <Mail className="h-16 w-16 text-electric-blue" />
                  <CheckCircle className="h-6 w-6 text-green-500 email-check" />
                </div>
              </div>
              <h1 className="success-title">Check Your Email!</h1>
              <p className="success-message">
                Your payment was successful. We've sent a magic link to:
              </p>
              <p className="success-email">{email}</p>
              
              <div className="success-instructions">
                <div className="instruction-item">
                  <span className="instruction-number">1</span>
                  <span>Check your inbox (and spam folder)</span>
                </div>
                <div className="instruction-item">
                  <span className="instruction-number">2</span>
                  <span>Click "Watch Washed Away Now"</span>
                </div>
                <div className="instruction-item">
                  <span className="instruction-number">3</span>
                  <span>Cast to your Smart TV with AirPlay or Chromecast</span>
                </div>
                <div className="instruction-item">
                  <span className="instruction-number">4</span>
                  <span>Enjoy 48 hours of streaming access!</span>
                </div>
              </div>

              <div className="success-note">
                <AlertCircle className="h-4 w-4" />
                <span>Link expires in 15 minutes. Use the same device to watch.</span>
              </div>

              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="success-button"
              >
                Return to Home
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="status-icon">
                <AlertCircle className="h-16 w-16 text-red-500" />
              </div>
              <h1 className="success-title">Something Went Wrong</h1>
              <p className="success-message">{errorMessage}</p>
              
              <div className="success-actions">
                <Button
                  onClick={() => navigate('/')}
                  className="success-button"
                  size="lg"
                >
                  Try Again
                </Button>
                <p className="success-help">
                  Need help? Contact support@washedaway.au
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
